import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MaterialReactTable } from 'material-react-table';
import {
  Box,
  createTheme,
  ThemeProvider,
  useTheme,
  Select,
  MenuItem,
  Checkbox,
  FormControl,
  InputLabel,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  TextField,
  CircularProgress,
  FormControlLabel,
} from '@mui/material';
import { runEmailReport, updateEmailReport, addEmailReport, deleteEmailReport } from '../../actions/apiEmailReports';
import ConfirmationModal from '../../components/ConfirmationDeleteModal';

const EmailReports = () => {
  const dispatch = useDispatch();
  const globalTheme = useTheme();
  const email_reports = useSelector((state) => state.settings_reports.email_reports || []);
  const related_installations = useSelector((state) => state.settings_dealerships.related_installations || []);
  const report_choices = useSelector((state) => state.settings_reports.report_types || []);
  const workflows = useSelector((state) => state.settings_departments.work_flows || []);
  const all_users = useSelector((state) => state.settings_users.all_users || []);
  const related_users = useSelector((state) => state.settings_users.related_users || []);
  const departments = useSelector((state) => state.settings_departments.original_departments || []);
  const [isLoading, setIsLoading] = useState(!email_reports.length);
  const [rowSelection, setRowSelection] = useState({});
  const [tableData, setTableData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteName, setDeleteName] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [runningReports, setRunningReports] = useState({});
  const tableRef = useRef();
  const [newReportData, setNewReportData] = useState({
    name: '',
    report_steps: '',
    report_dealerships: '',
    report_filters: '',
    report_run_day: '',
    report_run_time: '',
    report_run_frequency: '',
    report_choices: '',
    report_users: '',
    report_time_zone: '',
    report_range: '',
    active: true,
    include_hours: false,
    include_goals: false,
    group_report: false,
  });
  const [errors, setErrors] = useState({
    addEmailReportError: false,
  });

  // Debug logging
  useEffect(() => {
    // console.log('Raw email_reports:', JSON.stringify(email_reports, null, 2));
    // console.log('email_reports length:', email_reports.length);
    // console.log('related_installations:', related_installations);
    // console.log('report_choices:', report_choices);
    // console.log('workflows:', workflows);
    // console.log('departments:', departments);
    // console.log('related_users:', related_users);
    // console.log('all_users:', all_users);
    setIsLoading(!email_reports.length);
  }, [email_reports, related_installations, report_choices, workflows, departments, related_users, all_users]);

  // Update table data without forcing full re-render
  useEffect(() => {
    setTableData(
      email_reports.map((report) => ({
        id: [report.id || 0],
        name: [report.name || 'Unnamed Report'],
        report_choices: [report.report_choices || 'None'],
        report_dealerships: [report.report_dealerships ? report.report_dealerships.split(',').filter(Boolean).join(',') : 'None'],
        report_steps: [report.report_steps || ''],
        report_range: [report.report_range || ''],
        report_run_time: [report.report_run_time ? report.report_run_time.split(',').filter(Boolean).join(',') : 'None'],
        report_run_day: [report.report_run_day ? report.report_run_day.split(',').filter(Boolean).join(',') : 'None'],
        report_users: [report.report_users ? report.report_users.split(',').filter(Boolean).join(',') : 'None'],
        include_hours: [report.include_hours || false],
        include_goals: [report.include_goals || false],
        group_report: [report.group_report || false],
      }))
    );
  }, [email_reports]);

  const reportTimes = [
    '12:00 AM', '12:15 AM', '12:30 AM', '12:45 AM', '01:00 AM', '01:15 AM', '01:30 AM', '01:45 AM',
    '02:00 AM', '02:15 AM', '02:30 AM', '02:45 AM', '03:00 AM', '03:15 AM', '03:30 AM', '03:45 AM',
    '04:00 AM', '04:15 AM', '04:30 AM', '04:45 AM', '05:00 AM', '05:15 AM', '05:30 AM', '05:45 AM',
    '06:00 AM', '06:15 AM', '06:30 AM', '06:45 AM', '07:00 AM', '07:15 AM', '07:30 AM', '07:45 AM',
    '08:00 AM', '08:15 AM', '08:30 AM', '08:45 AM', '09:00 AM', '09:15 AM', '09:30 AM', '09:45 AM',
    '10:00 AM', '10:15 AM', '10:30 AM', '10:45 AM', '11:00 AM', '11:15 AM', '11:30 AM', '11:45 AM',
    '12:00 PM', '12:15 PM', '12:30 PM', '12:45 PM', '01:00 PM', '01:15 PM', '01:30 PM', '01:45 PM',
    '02:00 PM', '02:15 PM', '02:30 PM', '02:45 PM', '03:00 PM', '03:15 PM', '03:30 PM', '03:45 PM',
    '04:00 PM', '04:15 PM', '04:30 PM', '04:45 PM', '05:00 PM', '05:15 PM', '05:30 PM', '05:45 PM',
    '06:00 PM', '06:15 PM', '06:30 PM', '06:45 PM', '07:00 PM', '07:15 PM', '07:30 PM', '07:45 PM',
    '08:00 PM', '08:15 PM', '08:30 PM', '08:45 PM', '09:00 PM', '09:15 PM', '09:30 PM', '09:45 PM',
    '10:00 PM', '10:15 PM', '10:30 PM', '10:45 PM', '11:00 PM', '11:15 PM', '11:30 PM', '11:45 PM'
  ];

  const reportDays = [
    'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
    'First Day of the Month', 'Last Day of the Month'
  ];

  const reportRanges = [
    'last 7 days', 'last 14 days', 'last 30 days', 'last 60 days', 'last 90 days', 'month to date', 'last month'
  ];

  const uniqueDealerships = useMemo(
    () =>
      _.uniq(
        departments
          .filter((dept) => dept.dealerships && typeof dept.dealerships === 'string')
          .flatMap((dept) => dept.dealerships.split(',').map((d) => d.trim()))
      ).sort(),
    [departments],
  );

  const tableTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: 'light',
          primary: { main: '#007bff' },
        },
        components: {
          MuiTableHead: {
            styleOverrides: {
              root: {
                border: 'none',
              },
            },
          },
        },
      }),
    [globalTheme],
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        size: 120,
        enableEditing: false,
        filterVariant: 'text',
        filterFn: (row, id, filterValue) => {
          const nameValue = row.original.name[0] || '';
          return nameValue.toLowerCase().includes(filterValue.toLowerCase());
        },
        Cell: ({ row }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {row.original.name[0] || 'N/A'}
          </Box>
        ),
      },
      {
        accessorKey: 'report_choices',
        header: 'Report',
        size: 150,
        enableEditing: true,
        editVariant: 'select',
        filterVariant: 'select',
        filterFn: (row, id, filterValue) => {
          if (!filterValue) return true;
          const reportChoice = row.original.report_choices[0] || '';
          return reportChoice.toLowerCase() === filterValue.toLowerCase();
        },
        Filter: ({ column }) => (
          <FormControl fullWidth sx={{ minWidth: 120 }}>
            <InputLabel>{column.columnDef.header}</InputLabel>
            <Select
              value={column.getFilterValue() || ''}
              onChange={(e) => column.setFilterValue(e.target.value || undefined)}
              label={column.columnDef.header}
            >
              <MenuItem value="">All</MenuItem>
              {report_choices.map((option) => (
                <MenuItem key={option.id} value={option.name}>
                  {option.title || option.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ),
        muiEditTextFieldProps: ({ row, table }) => ({
          select: true,
          value: row.original.report_choices[0] || '',
          onChange: (event) => {
            const report_choices = event.target.value;
            const data = { report_choices };
            dispatch(updateEmailReport(row.original.id[0], data, 'report_choices')).then(() => {
              setTableData((prev) =>
                prev.map((item) =>
                  item.id[0] === row.original.id[0] ? { ...item, report_choices: [report_choices] } : item
                )
              );
              table.setEditingCell(null);
            });
          },
          children: report_choices.map((option) => (
            <MenuItem key={option.id} value={option.name}>
              {option.title || option.name}
            </MenuItem>
          )),
        }),
        Cell: ({ row }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {row.original.report_choices[0] || 'None'}
          </Box>
        ),
      },
      {
        accessorKey: 'report_dealerships',
        header: 'Dealerships',
        enableEditing: true,
        editVariant: 'select',
        filterVariant: 'select',
        filterFn: (row, id, filterValue) => {
          if (!filterValue) return true;
          const dealershipArray = row.original.report_dealerships[0] ? row.original.report_dealerships[0].split(',').map((d) => d.trim()) : [];
          return dealershipArray.includes(filterValue);
        },
        Filter: ({ column }) => (
          <FormControl fullWidth sx={{ minWidth: 120 }}>
            <InputLabel>{column.columnDef.header}</InputLabel>
            <Select
              value={column.getFilterValue() || ''}
              onChange={(e) => column.setFilterValue(e.target.value || undefined)}
              label={column.columnDef.header}
            >
              <MenuItem value="">All</MenuItem>
              {uniqueDealerships.map((dealership) => (
                <MenuItem key={dealership} value={dealership}>
                  {dealership}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ),
        muiEditTextFieldProps: ({ row, table }) => {
          const dealershipArray = row.original.report_dealerships[0] ? row.original.report_dealerships[0].split(',').filter(Boolean) : [];
          return {
            select: true,
            multiple: true,
            value: dealershipArray,
            SelectProps: {
              renderValue: (selected) => (Array.isArray(selected) ? selected.join(', ') : ''),
              MenuProps: { PaperProps: { style: { maxHeight: 300, minWidth: 400 } } },
              sx: { zIndex: 1000 },
              multiple: true,
            },
            children: uniqueDealerships.map((dealership) => (
              <MenuItem key={dealership} value={dealership}>
                <Checkbox checked={dealershipArray.includes(dealership)} />
                {dealership}
              </MenuItem>
            )),
            onChange: (event) => {
              const selectedValues = Array.isArray(event.target.value) ? event.target.value : [];
              setTableData((prev) =>
                prev.map((item) =>
                  item.id[0] === row.original.id[0] ? { ...item, report_dealerships: [selectedValues.join(',')] } : item
                )
              );
            },
            onClick: (event) => event.stopPropagation(),
            onBlur: () => {
              const data = { report_dealerships: dealershipArray.join(',') };
              dispatch(updateEmailReport(row.original.id[0], data, 'report_dealerships'));
              table.setEditingCell(null);
            },
          };
        },
        Cell: ({ row }) => {
          const dealerships = row.original.report_dealerships[0] ? row.original.report_dealerships[0].split(',').filter(Boolean) : [];
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem', whiteSpace: 'normal', wordWrap: 'break-word', maxWidth: 200 }}>
              {dealerships.length > 0 ? dealerships.join(', ') : 'None'}
            </Box>
          );
        },
      },
      {
        accessorKey: 'report_steps',
        header: 'Steps / Workflows',
        enableEditing: true,
        editVariant: 'select',
        filterVariant: 'select',
        filterFn: (row, id, filterValue) => {
          if (!filterValue) return true;
          const stepsArray = row.original.report_steps[0] ? row.original.report_steps[0].split(',').map(Number) : [];
          return stepsArray.includes(Number(filterValue));
        },
        Filter: ({ column }) => (
          <FormControl fullWidth sx={{ minWidth: 120 }}>
            <InputLabel>{column.columnDef.header}</InputLabel>
            <Select
              value={column.getFilterValue() || ''}
              onChange={(e) => column.setFilterValue(e.target.value || undefined)}
              label={column.columnDef.header}
            >
              <MenuItem value="">All</MenuItem>
              {(workflows.length > 0 ? workflows : departments)
                .slice()
                .sort((a, b) => (a.order != null ? a.order : Infinity) - (b.order != null ? b.order : Infinity))
                .map((item) => (
                  <MenuItem key={item.id} value={item.id.toString()}>
                    {(item.name || 'Unnamed').toUpperCase()}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        ),
        muiEditTextFieldProps: ({ row, table }) => {
          const stepsArray = row.original.report_steps[0] ? row.original.report_steps[0].split(',').filter(Boolean).map(Number) : [];
          const isWorkflowReport = ['workflow_summary', 'workflow_vehicles', 'workflow_departments'].includes(row.original.report_choices[0]);
          const items = isWorkflowReport && workflows.length > 0 ? workflows : departments;
          return {
            select: true,
            multiple: true,
            value: stepsArray,
            SelectProps: {
              renderValue: (selected) => {
                const selectedNames = selected.map((id) => {
                  const item = items.find((d) => d.id === id);
                  return item ? (item.name || 'Unnamed').toUpperCase() : id;
                });
                return Array.isArray(selected) ? selectedNames.join(', ') : '';
              },
              MenuProps: { PaperProps: { style: { maxHeight: 300, minWidth: 400 } } },
              sx: { zIndex: 1000 },
              multiple: true,
            },
            children: items
              .slice()
              .sort((a, b) => (a.order != null ? a.order : Infinity) - (b.order != null ? b.order : Infinity))
              .map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  <Checkbox checked={stepsArray.includes(item.id)} />
                  {(item.name || 'Unnamed').toUpperCase()}
                </MenuItem>
              )),
            onChange: (event) => {
              const selectedValues = Array.isArray(event.target.value) ? event.target.value : [];
              setTableData((prev) =>
                prev.map((item) =>
                  item.id[0] === row.original.id[0] ? { ...item, report_steps: [selectedValues.join(',')] } : item
                )
              );
            },
            onClick: (event) => event.stopPropagation(),
            onBlur: () => {
              const data = { report_steps: stepsArray.join(',') };
              dispatch(updateEmailReport(row.original.id[0], data, 'report_steps'));
              table.setEditingCell(null);
            },
          };
        },
        Cell: ({ row }) => {
          const steps = row.original.report_steps[0] ? row.original.report_steps[0].split(',').filter(Boolean).map(Number) : [];
          const isWorkflowReport = ['workflow_summary', 'workflow_vehicles', 'workflow_departments'].includes(row.original.report_choices[0]);
          const items = isWorkflowReport && workflows.length > 0 ? workflows : departments;
          const displayNames = steps
            .map((id) => {
              const item = items.find((d) => d.id === id);
              return item ? (item.name || 'Unnamed').toUpperCase() : id;
            })
            .join(', ');
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem', whiteSpace: 'normal', wordWrap: 'break-word', maxWidth: 200 }}>
              {displayNames || 'None'}
            </Box>
          );
        },
      },
      {
        accessorKey: 'report_range',
        header: 'Range',
        size: 150,
        enableEditing: true,
        editVariant: 'select',
        filterVariant: 'select',
        filterFn: (row, id, filterValue) => {
          if (!filterValue) return true;
          const rangeArray = row.original.report_range[0] ? row.original.report_range[0].split(',').filter(Boolean) : [];
          return rangeArray.includes(filterValue);
        },
        Filter: ({ column }) => (
          <FormControl fullWidth sx={{ minWidth: 120 }}>
            <InputLabel>{column.columnDef.header}</InputLabel>
            <Select
              value={column.getFilterValue() || ''}
              onChange={(e) => column.setFilterValue(e.target.value || undefined)}
              label={column.columnDef.header}
            >
              <MenuItem value="">All</MenuItem>
              {reportRanges.map((range) => (
                <MenuItem key={range} value={range}>
                  {range}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ),
        muiEditTextFieldProps: ({ row, table }) => {
          const rangeArray = row.original.report_range[0] ? row.original.report_range[0].split(',').filter(Boolean) : [];
          return {
            select: true,
            multiple: true,
            value: rangeArray,
            SelectProps: {
              renderValue: (selected) => (Array.isArray(selected) ? selected.join(', ') : ''),
              MenuProps: { PaperProps: { style: { maxHeight: 300, minWidth: 400 } } },
              sx: { zIndex: 1000 },
              multiple: true,
            },
            children: reportRanges.map((range) => (
              <MenuItem key={range} value={range}>
                <Checkbox checked={rangeArray.includes(range)} />
                {range}
              </MenuItem>
            )),
            onChange: (event) => {
              const selectedValues = Array.isArray(event.target.value) ? event.target.value : [];
              setTableData((prev) =>
                prev.map((item) =>
                  item.id[0] === row.original.id[0] ? { ...item, report_range: [selectedValues.join(',')] } : item
                )
              );
            },
            onClick: (event) => event.stopPropagation(),
            onBlur: () => {
              const data = { report_range: rangeArray.join(',') };
              dispatch(updateEmailReport(row.original.id[0], data, 'report_range'));
              table.setEditingCell(null);
            },
          };
        },
        Cell: ({ row }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem', whiteSpace: 'normal', wordWrap: 'break-word', maxWidth: 200 }}>
            {row.original.report_range[0] ? row.original.report_range[0].split(',').filter(Boolean).join(', ') : 'None'}
          </Box>
        ),
      },
      {
        accessorKey: 'report_run_time',
        header: 'Run Times',
        enableEditing: true,
        editVariant: 'select',
        filterVariant: 'select',
        filterFn: (row, id, filterValue) => {
          if (!filterValue) return true;
          const timesArray = row.original.report_run_time[0] ? row.original.report_run_time[0].split(',').filter(Boolean) : [];
          return timesArray.includes(filterValue);
        },
        Filter: ({ column }) => (
          <FormControl fullWidth sx={{ minWidth: 120 }}>
            <InputLabel>{column.columnDef.header}</InputLabel>
            <Select
              value={column.getFilterValue() || ''}
              onChange={(e) => column.setFilterValue(e.target.value || undefined)}
              label={column.columnDef.header}
            >
              <MenuItem value="">All</MenuItem>
              {reportTimes.map((time) => (
                <MenuItem key={time} value={time}>
                  {time}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ),
        muiEditTextFieldProps: ({ row, table }) => {
          const timesArray = row.original.report_run_time[0] ? row.original.report_run_time[0].split(',').filter(Boolean) : [];
          return {
            select: true,
            multiple: true,
            value: timesArray,
            SelectProps: {
              renderValue: (selected) => (Array.isArray(selected) ? selected.join(', ') : ''),
              MenuProps: { PaperProps: { style: { maxHeight: 300, minWidth: 400 } } },
              sx: { zIndex: 1000 },
              multiple: true,
            },
            children: reportTimes.map((time) => (
              <MenuItem key={time} value={time}>
                <Checkbox checked={timesArray.includes(time)} />
                {time}
              </MenuItem>
            )),
            onChange: (event) => {
              const selectedValues = Array.isArray(event.target.value) ? event.target.value : [];
              setTableData((prev) =>
                prev.map((item) =>
                  item.id[0] === row.original.id[0] ? { ...item, report_run_time: [selectedValues.join(',')] } : item
                )
              );
            },
            onClick: (event) => event.stopPropagation(),
            onBlur: () => {
              const data = { report_run_time: timesArray.join(',') };
              dispatch(updateEmailReport(row.original.id[0], data, 'report_run_time'));
              table.setEditingCell(null);
            },
          };
        },
        Cell: ({ row }) => {
          const times = row.original.report_run_time[0] ? row.original.report_run_time[0].split(',').filter(Boolean) : [];
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem', whiteSpace: 'normal', wordWrap: 'break-word', maxWidth: 200 }}>
              {times.length > 0 ? times.join(', ') : 'None'}
            </Box>
          );
        },
      },
      {
        accessorKey: 'report_run_day',
        header: 'Run Days',
        enableEditing: true,
        editVariant: 'select',
        filterVariant: 'select',
        filterFn: (row, id, filterValue) => {
          if (!filterValue) return true;
          const daysArray = row.original.report_run_day[0] ? row.original.report_run_day[0].split(',').filter(Boolean) : [];
          return daysArray.includes(filterValue);
        },
        Filter: ({ column }) => (
          <FormControl fullWidth sx={{ minWidth: 120 }}>
            <InputLabel>{column.columnDef.header}</InputLabel>
            <Select
              value={column.getFilterValue() || ''}
              onChange={(e) => column.setFilterValue(e.target.value || undefined)}
              label={column.columnDef.header}
            >
              <MenuItem value="">All</MenuItem>
              {reportDays.map((day) => (
                <MenuItem key={day} value={day}>
                  {day}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ),
        muiEditTextFieldProps: ({ row, table }) => {
          const daysArray = row.original.report_run_day[0] ? row.original.report_run_day[0].split(',').filter(Boolean) : [];
          return {
            select: true,
            multiple: true,
            value: daysArray,
            SelectProps: {
              renderValue: (selected) => (Array.isArray(selected) ? selected.join(', ') : ''),
              MenuProps: { PaperProps: { style: { maxHeight: 300, minWidth: 400 } } },
              sx: { zIndex: 1000 },
              multiple: true,
            },
            children: reportDays.map((day) => (
              <MenuItem key={day} value={day}>
                <Checkbox checked={daysArray.includes(day)} />
                {day}
              </MenuItem>
            )),
            onChange: (event) => {
              const selectedValues = Array.isArray(event.target.value) ? event.target.value : [];
              setTableData((prev) =>
                prev.map((item) =>
                  item.id[0] === row.original.id[0] ? { ...item, report_run_day: [selectedValues.join(',')] } : item
                )
              );
            },
            onClick: (event) => event.stopPropagation(),
            onBlur: () => {
              const data = { report_run_day: daysArray.join(',') };
              dispatch(updateEmailReport(row.original.id[0], data, 'report_run_day'));
              table.setEditingCell(null);
            },
          };
        },
        Cell: ({ row }) => {
          const days = row.original.report_run_day[0] ? row.original.report_run_day[0].split(',').filter(Boolean) : [];
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem', whiteSpace: 'normal', wordWrap: 'break-word', maxWidth: 200 }}>
              {days.length > 0 ? days.join(', ') : 'None'}
            </Box>
          );
        },
      },
      {
        accessorKey: 'report_users',
        header: 'Recipients',
        enableEditing: true,
        editVariant: 'select',
        filterVariant: 'select',
        filterFn: (row, id, filterValue) => {
          if (!filterValue) return true;
          const usersArray = row.original.report_users[0] ? row.original.report_users[0].split(',').filter(Boolean) : [];
          return usersArray.includes(filterValue);
        },
        Filter: ({ column }) => (
          <FormControl fullWidth sx={{ minWidth: 120 }}>
            <InputLabel>{column.columnDef.header}</InputLabel>
            <Select
              value={column.getFilterValue() || ''}
              onChange={(e) => column.setFilterValue(e.target.value || undefined)}
              label={column.columnDef.header}
            >
              <MenuItem value="">All</MenuItem>
              {(related_users.length > 0 ? related_users : all_users).map((user) => (
                <MenuItem key={user.id} value={user.user_name}>
                  {user.user_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ),
        muiEditTextFieldProps: ({ row, table }) => {
          const usersArray = row.original.report_users[0] ? row.original.report_users[0].split(',').filter(Boolean) : [];
          return {
            select: true,
            multiple: true,
            value: usersArray,
            SelectProps: {
              renderValue: (selected) => (Array.isArray(selected) ? selected.join(', ') : ''),
              MenuProps: { PaperProps: { style: { maxHeight: 300, minWidth: 400 } } },
              sx: { zIndex: 1000 },
              multiple: true,
            },
            children: (related_users.length > 0 ? related_users : all_users).map((user) => (
              <MenuItem key={user.id} value={user.user_name}>
                <Checkbox checked={usersArray.includes(user.user_name)} />
                {user.user_name}
              </MenuItem>
            )),
            onChange: (event) => {
              const selectedValues = Array.isArray(event.target.value) ? event.target.value : [];
              setTableData((prev) =>
                prev.map((item) =>
                  item.id[0] === row.original.id[0] ? { ...item, report_users: [selectedValues.join(',')] } : item
                )
              );
            },
            onClick: (event) => event.stopPropagation(),
            onBlur: () => {
              const data = { report_users: usersArray.join(',') };
              dispatch(updateEmailReport(row.original.id[0], data, 'report_users'));
              table.setEditingCell(null);
            },
          };
        },
        Cell: ({ row }) => {
          const users = row.original.report_users[0] ? row.original.report_users[0].split(',').filter(Boolean) : [];
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem', whiteSpace: 'normal', wordWrap: 'break-word', maxWidth: 200 }}>
              {users.length > 0 ? users.join(', ') : 'None'}
            </Box>
          );
        },
      },
      {
        accessorKey: 'include_hours',
        header: 'Include Hours',
        size: 100,
        enableEditing: true,
        Cell: ({ row }) => (
          <Checkbox
            checked={row.original.include_hours[0] || false}
            onChange={(event) => {
              const data = { include_hours: event.target.checked };
              dispatch(updateEmailReport(row.original.id[0], data, 'include_hours')).then(() => {
                setTableData((prev) =>
                  prev.map((item) =>
                    item.id[0] === row.original.id[0] ? { ...item, include_hours: [event.target.checked] } : item
                  )
                );
              });
            }}
          />
        ),
      },
      {
        accessorKey: 'include_goals',
        header: 'Include Goals',
        size: 100,
        enableEditing: true,
        Cell: ({ row }) => (
          <Checkbox
            checked={row.original.include_goals[0] || false}
            onChange={(event) => {
              const data = { include_goals: event.target.checked };
              dispatch(updateEmailReport(row.original.id[0], data, 'include_goals')).then(() => {
                setTableData((prev) =>
                  prev.map((item) =>
                    item.id[0] === row.original.id[0] ? { ...item, include_goals: [event.target.checked] } : item
                  )
                );
              });
            }}
          />
        ),
      },
      {
        accessorKey: 'group_report',
        header: 'Group Report',
        size: 100,
        enableEditing: true,
        Cell: ({ row }) => (
          <Checkbox
            checked={row.original.group_report[0] || false}
            onChange={(event) => {
              const data = { group_report: event.target.checked };
              dispatch(updateEmailReport(row.original.id[0], data, 'group_report')).then(() => {
                setTableData((prev) =>
                  prev.map((item) =>
                    item.id[0] === row.original.id[0] ? { ...item, group_report: [event.target.checked] } : item
                  )
                );
              });
            }}
          />
        ),
      },
      {
        accessorKey: 'run_report',
        header: 'Run Report',
        size: 80,
        enableEditing: false,
        Cell: ({ row }) => (
          runningReports[row.original.id[0]] ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <CircularProgress size={20} />
              <span>Sending...</span>
            </Box>
          ) : (
            <Button
              variant="contained"
              color="success"
              size="small"
              disabled={runningReports[row.original.id[0]]}
              onClick={() => {
                setRunningReports((prev) => ({ ...prev, [row.original.id[0]]: true }));
                dispatch(runEmailReport(row.original.id[0], 'email run report')).then((resp) => {
                  setRunningReports((prev) => ({ ...prev, [row.original.id[0]]: false }));
                });
              }}
            >
              <i className="fal fa-play" />
            </Button>
          )
        ),
      },
      {
        accessorKey: 'delete',
        header: '',
        size: 80,
        enableEditing: false,
        Cell: ({ row }) => (
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={() => {
              setDeleteId(row.original.id[0]);
              setDeleteName(row.original.name[0]);
              setModalOpen(true);
            }}
          >
            Delete
          </Button>
        ),
      },
    ],
    [related_installations, report_choices, workflows, related_users, all_users, departments, uniqueDealerships, dispatch, runningReports],
  );

  const handleConfirmDelete = () => {
    dispatch(deleteEmailReport(deleteId, 'deleteEmailReportError')).then(() => {
      setTableData((prev) => prev.filter((item) => item.id[0] !== deleteId));
    });
    setModalOpen(false);
    setDeleteId(null);
    setDeleteName(null);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setDeleteId(null);
    setDeleteName(null);
  };

  const handleOpenAddModal = () => {
    setAddModalOpen(true);
    setNewReportData({
      name: '',
      report_steps: '',
      report_dealerships: '',
      report_filters: '',
      report_run_day: '',
      report_run_time: '',
      report_run_frequency: '',
      report_choices: '',
      report_users: '',
      report_time_zone: '',
      report_range: '',
      active: true,
      include_hours: false,
      include_goals: false,
      group_report: false,
    });
    setErrors((prev) => ({ ...prev, addEmailReportError: false }));
  };

  const handleCloseAddModal = () => {
    setAddModalOpen(false);
    setErrors((prev) => ({ ...prev, addEmailReportError: false }));
  };

  const handleAddEmailReport = () => {
    if (!newReportData.name.trim()) {
      setErrors((prev) => ({ ...prev, addEmailReportError: true }));
      return;
    }

    setErrors((prev) => ({ ...prev, addEmailReportError: false }));

    const cleanedData = {
      ...newReportData,
      report_dealerships: newReportData.report_dealerships.split(',').filter(Boolean).join(','),
      report_steps: newReportData.report_steps.split(',').filter(Boolean).join(','),
      report_run_time: newReportData.report_run_time.split(',').filter(Boolean).join(','),
      report_run_day: newReportData.report_run_day.split(',').filter(Boolean).join(','),
      report_users: newReportData.report_users.split(',').filter(Boolean).join(','),
      report_range: newReportData.report_range.split(',').filter(Boolean).join(','),
    };

    dispatch(addEmailReport(cleanedData, {})).then((resp) => {
      if (resp === 'success') {
        setErrors((prev) => ({ ...prev, addEmailReportError: false }));
        handleCloseAddModal();
      } else {
        setErrors((prev) => ({ ...prev, addEmailReportError: true }));
      }
    });
  };

  const handleNewReportChange = (field, value) => {
    setNewReportData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="container-fluid">
      <style>
        {`
          .setscroll {
            min-height: 400px;
            overflow-y: auto;
            width: 100%;
          }
        `}
      </style>
      <ConfirmationModal
        isOpen={modalOpen}
        onRequestClose={handleCloseModal}
        onConfirm={handleConfirmDelete}
        message={`Are you sure you want to delete the email report "${deleteName}"?`}
      />
      <Dialog open={addModalOpen} onClose={handleCloseAddModal}>
        <DialogTitle>Add Email Report</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Report Name"
            type="text"
            fullWidth
            value={newReportData.name}
            onChange={(e) => handleNewReportChange('name', e.target.value)}
            error={errors.addEmailReportError}
            helperText={errors.addEmailReportError ? 'Please enter a valid report name' : ''}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Report Choice</InputLabel>
            <Select
              value={newReportData.report_choices}
              onChange={(e) => handleNewReportChange('report_choices', e.target.value)}
            >
              <MenuItem value="">None</MenuItem>
              {report_choices.map((option) => (
                <MenuItem key={option.id} value={option.name}>
                  {option.title || option.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Steps / Workflows</InputLabel>
            <Select
              multiple
              value={newReportData.report_steps.split(',').filter(Boolean).map(Number)}
              onChange={(e) => handleNewReportChange('report_steps', e.target.value.join(','))}
              renderValue={(selected) => {
                const isWorkflowReport = ['workflow_summary', 'workflow_vehicles', 'workflow_departments'].includes(newReportData.report_choices);
                const items = isWorkflowReport && workflows.length > 0 ? workflows : departments;
                const selectedNames = selected.map((id) => {
                  const item = items.find((d) => d.id === id);
                  return item ? (item.name || 'Unnamed').toUpperCase() : id;
                });
                return selectedNames.join(', ');
              }}
              MenuProps={{ PaperProps: { style: { maxHeight: 300, minWidth: 400 } } }}
            >
              {(newReportData.report_choices && ['workflow_summary', 'workflow_vehicles', 'workflow_departments'].includes(newReportData.report_choices) && workflows.length > 0 ? workflows : departments)
                .slice()
                .sort((a, b) => (a.order != null ? a.order : Infinity) - (b.order != null ? b.order : Infinity))
                .map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    <Checkbox checked={newReportData.report_steps.split(',').map(Number).includes(item.id)} />
                    {(item.name || 'Unnamed').toUpperCase()}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Dealerships</InputLabel>
            <Select
              multiple
              value={newReportData.report_dealerships.split(',').filter(Boolean)}
              onChange={(e) => handleNewReportChange('report_dealerships', e.target.value.join(','))}
              renderValue={(selected) => (selected.length > 0 ? selected.join(', ') : 'None')}
              MenuProps={{ PaperProps: { style: { maxHeight: 300, minWidth: 400 } } }}
            >
              {uniqueDealerships.map((dealership) => (
                <MenuItem key={dealership} value={dealership}>
                  <Checkbox checked={newReportData.report_dealerships.split(',').includes(dealership)} />
                  {dealership}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Time Range</InputLabel>
            <Select
              multiple
              value={newReportData.report_range.split(',').filter(Boolean)}
              onChange={(e) => handleNewReportChange('report_range', e.target.value.join(','))}
              renderValue={(selected) => (selected.length > 0 ? selected.join(', ') : 'None')}
              MenuProps={{ PaperProps: { style: { maxHeight: 300, minWidth: 400 } } }}
            >
              {reportRanges.map((range) => (
                <MenuItem key={range} value={range}>
                  <Checkbox checked={newReportData.report_range.split(',').includes(range)} />
                  {range}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Run Times</InputLabel>
            <Select
              multiple
              value={newReportData.report_run_time.split(',').filter(Boolean)}
              onChange={(e) => handleNewReportChange('report_run_time', e.target.value.join(','))}
              renderValue={(selected) => (selected.length > 0 ? selected.join(', ') : 'None')}
              MenuProps={{ PaperProps: { style: { maxHeight: 300, minWidth: 400 } } }}
            >
              {reportTimes.map((time) => (
                <MenuItem key={time} value={time}>
                  <Checkbox checked={newReportData.report_run_time.split(',').includes(time)} />
                  {time}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Run Days</InputLabel>
            <Select
              multiple
              value={newReportData.report_run_day.split(',').filter(Boolean)}
              onChange={(e) => handleNewReportChange('report_run_day', e.target.value.join(','))}
              renderValue={(selected) => (selected.length > 0 ? selected.join(', ') : 'None')}
              MenuProps={{ PaperProps: { style: { maxHeight: 300, minWidth: 400 } } }}
            >
              {reportDays.map((day) => (
                <MenuItem key={day} value={day}>
                  <Checkbox checked={newReportData.report_run_day.split(',').includes(day)} />
                  {day}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Recipients</InputLabel>
            <Select
              multiple
              value={newReportData.report_users.split(',').filter(Boolean)}
              onChange={(e) => handleNewReportChange('report_users', e.target.value.join(','))}
              renderValue={(selected) => (selected.length > 0 ? selected.join(', ') : 'None')}
              MenuProps={{ PaperProps: { style: { maxHeight: 300, minWidth: 400 } } }}
            >
              {(related_users.length > 0 ? related_users : all_users).map((user) => (
                <MenuItem key={user.id} value={user.user_name}>
                  <Checkbox checked={newReportData.report_users.split(',').includes(user.user_name)} />
                  {user.user_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={newReportData.include_hours}
                  onChange={(e) => handleNewReportChange('include_hours', e.target.checked)}
                />
              }
              label="Include Hours"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={newReportData.include_goals}
                  onChange={(e) => handleNewReportChange('include_goals', e.target.checked)}
                />
              }
              label="Include Goals"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={newReportData.group_report}
                  onChange={(e) => handleNewReportChange('group_report', e.target.checked)}
                />
              }
              label="Group Report"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddModal} color="error">
            Cancel
          </Button>
          <Button onClick={handleAddEmailReport} color="success">
            Add
          </Button>
        </DialogActions>
      </Dialog>
      <div className="col-md-12">
        <div className="row">
          <div className="card col-md-12">
            <div className="card-body">
              <h3 className="text-center pad-top-1">Automated Email Reports</h3>
              {errors.addEmailReportError && (
                <div className="alert alert-danger">Error adding email report.</div>
              )}
              {isLoading && (
                <div className="text-center">
                  <CircularProgress />
                  <p>Loading email reports...</p>
                </div>
              )}
              {!isLoading && tableData.length === 0 && (
                <div className="alert alert-info">No email reports available.</div>
              )}
              <div className="d-flex flex-column">
                <div className="p-2 mb-3 d-flex align-items-center gap-3">
                  <Button
                    variant="contained"
                    color="success"
                    className="btn btn-success"
                    onClick={handleOpenAddModal}
                  >
                    Add Email Report
                  </Button>
                </div>
                <div className="p-2 list-group setscroll">
                  <ThemeProvider theme={tableTheme}>
                    <MaterialReactTable
                      tableRef={tableRef}
                      columns={columns}
                      data={tableData}
                      editDisplayMode="cell"
                      enableEditing
                      enableGrouping
                      enableColumnFilters
                      state={{ rowSelection }}
                      initialState={{
                        showColumnFilters: true,
                        pagination: { pageSize: 100 },
                      }}
                      muiTablePaperProps={{
                        sx: { width: '100%' },
                      }}
                      renderDetailPanel={({ row }) => (
                        <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
                          <div>No details available for email report</div>
                        </Box>
                      )}
                    />
                  </ThemeProvider>
                </div>
              </div>
            </div>
            <div className="card-footer" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailReports;